export interface TimecardHolder {
  id: string;
  version: number;
  householdId: string;
  name: string;
  timeZone: string;
  weekStartsOn: string;
  createdTimestamp: string;
  deletedTimestamp: string | null;
}

export interface TimecardProject {
  id: string | null;
  version: number | null;
  timecardHolderId: string;
  name: string;
  active: boolean;
  weeklyTargetMinutes: number | null;
  weeklyMaxMinutes: number | null;
  sortOrder: number | null;
  reportDetails: string | null;
  hourlyRateCents: number | null;
  createdTimestamp: string | null;
  deletedTimestamp: string | null;
}

export interface TimecardEntry {
  id: string;
  version: number;
  timecardProjectId: string;
  startTimestamp: string;
  stopTimestamp: string | null;
  description: string | null;
  billable: boolean;
}

export interface TimecardSnapshot {
  id: string;
  timecardHolder: TimecardHolder;
  projects: TimecardProject[];
  weekEntries: TimecardEntry[];
  runningEntry: TimecardEntry | null;
  weekStart: string;
  weekEnd: string;
  todayStart: string;
  todayEnd: string;
  serverNow: string;
}

export interface TimecardDay {
  date: string;
  start: string;
  end: string;
}

export interface TimecardEntryRange {
  entries: TimecardEntry[];
  days: TimecardDay[];
}

export function dayLabel(date: string): string {
  const value = new Date(date + 'T12:00:00Z');
  const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'UTC' }).format(value);
  const monthDay = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(value);
  return `${weekday} - ${monthDay}`;
}

export interface ReportDay {
  date: string;
  projectId: string;
  projectName: string;
  entryBillable: boolean;
  seconds: number;
  billableSeconds: number;
  freeSeconds: number;
  descriptions: string[];
}

export interface TimecardReport {
  name: string;
  timeZone: string;
  startDate: string;
  endDate: string;
  generatedAt: string;
  includesRunningEntry: boolean;
  billingMaxMinutes: number | null;
  totalSeconds: number;
  billableSeconds: number;
  freeSeconds: number;
  days: ReportDay[];
}

export const weekdays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY']
  .map(value => ({ value, text: value.charAt(0) + value.slice(1).toLowerCase() }));

// Calendar strings are formatted in the holder's zone, never the browser's zone.
export function localTime(instant: string | number, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(instant));
  const value = (type: string) => parts.find(part => part.type === type)?.value || '';
  return `${value('year')}-${value('month')}-${value('day')}T${value('hour')}:${value('minute')}:${value('second')}`;
}

export function utcOffset(instant: string | number, timeZone: string): string {
  const seconds = Math.floor(new Date(instant).getTime() / 1000) * 1000;
  const minutes = Math.round((Date.parse(localTime(seconds, timeZone) + 'Z') - seconds) / 60000);
  return `${minutes < 0 ? '-' : '+'}${String(Math.floor(Math.abs(minutes) / 60)).padStart(2, '0')}:${String(Math.abs(minutes) % 60).padStart(2, '0')}`;
}

// Find the real instants matching a local time. Repeated clock times have two offsets;
// spring-forward gaps have none and are rejected by the server when saving.
export function localTimeOffsets(value: string | null, timeZone: string): string[] {
  if (!value || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) return [];
  const local = value.length === 16 ? value + ':00' : value;
  const nominal = Date.parse(local + 'Z');
  if (!Number.isFinite(nominal)) return [];
  const minutes = (offset: string) => (offset[0] === '-' ? -1 : 1) * (Number(offset.slice(1, 3)) * 60 + Number(offset.slice(4, 6)));
  const candidates = new Set<string>();
  // Sampling either side captures both offsets, including half-hour DST changes.
  for (let hours = -36; hours <= 36; hours += 6)
    candidates.add(utcOffset(nominal + hours * 3600000, timeZone));
  return Array.from(candidates)
    .filter(offset => localTime(nominal - minutes(offset) * 60000, timeZone) === local)
    .sort((a, b) => minutes(b) - minutes(a));
}

export function lastMonthRange(today: string): { startDate: string; endDate: string } {
  const endDate = shiftDate(today.slice(0, 7) + '-01', -1);
  return { startDate: endDate.slice(0, 7) + '-01', endDate };
}

export function shiftDate(date: string, days: number): string {
  const value = new Date(date + 'T12:00:00Z');
  value.setUTCDate(value.getUTCDate() + days);
  return value.toISOString().slice(0, 10);
}

export function duration(seconds: number, showSeconds = false): string {
  const value = Math.max(0, Math.floor(seconds));
  return `${Math.floor(value / 3600)}h ${String(Math.floor(value / 60) % 60).padStart(2, '0')}m` +
    (showSeconds ? ` ${String(value % 60).padStart(2, '0')}s` : '');
}

export function entrySeconds(entry: TimecardEntry, now: number, start = 0, end = Infinity): number {
  return Math.max(0, Math.min(entry.stopTimestamp ? Date.parse(entry.stopTimestamp) : now, end) -
    Math.max(Date.parse(entry.startTimestamp), start)) / 1000;
}

export interface DaySegment {
  projectId: string | null;
  kind?: 'untracked' | 'remaining';
  start: number;
  end: number;
  left: number;
  width: number;
}

export function daySegments(entries: TimecardEntry[], now: number, start: number, end: number): DaySegment[] {
  if (!(end > start)) return [];
  const segments: DaySegment[] = [];
  entries.slice().sort((a, b) => Date.parse(a.startTimestamp) - Date.parse(b.startTimestamp)).forEach(entry => {
    const from = Math.max(start, Date.parse(entry.startTimestamp));
    const to = Math.min(end, now, entry.stopTimestamp ? Date.parse(entry.stopTimestamp) : now);
    if (!(to > from)) return;
    const previous = segments[segments.length - 1];
    if (previous && previous.projectId === entry.timecardProjectId && previous.end === from) {
      previous.end = to;
      previous.width = (to - previous.start) / (end - start) * 100;
    } else {
      segments.push({
        projectId: entry.timecardProjectId,
        start: from,
        end: to,
        left: (from - start) / (end - start) * 100,
        width: (to - from) / (end - start) * 100,
      });
    }
  });
  return segments;
}

export function dayBackgroundSegments(segments: DaySegment[], now: number, start: number, end: number): DaySegment[] {
  if (!(end > start)) return [];
  const until = Math.max(start, Math.min(now, end));
  const gaps: DaySegment[] = [];
  const add = (from: number, to: number, kind: 'untracked' | 'remaining') => {
    if (to <= from) return;
    gaps.push({
      projectId: null,
      kind,
      start: from,
      end: to,
      left: (from - start) / (end - start) * 100,
      width: (to - from) / (end - start) * 100,
    });
  };
  let cursor = start;
  for (const segment of segments.slice().sort((a, b) => a.start - b.start)) {
    add(cursor, Math.min(segment.start, until), 'untracked');
    cursor = Math.max(cursor, Math.min(segment.end, until));
    if (cursor >= until) break;
  }
  add(cursor, until, 'untracked');
  add(until, end, 'remaining');
  return gaps;
}

export function projectColor(id: string | null, firstProjectId?: string | null, secondProjectId?: string | null): string {
  // Primary green from https://www.hopdox.com/.
  if (id && id === firstProjectId) return '#92c74f';
  // Light blue-teal sampled from the Oktiv reference text.
  if (id && id === secondProjectId) return '#5eead4';
  let hash = 2166136261;
  for (const character of id || '') hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
  return `hsl(${(hash >>> 0) % 360}, 65%, 62%)`;
}

export function dayTicks(start: number, end: number, timeZone: string): { label: string; left: number }[] {
  const date = localTime(start, timeZone).slice(0, 10);
  const ticks = [{ label: '12 am', left: 0 }];
  for (const hour of [6, 12, 18]) {
    const value = `${date}T${String(hour).padStart(2, '0')}:00:00`;
    const offset = localTimeOffsets(value, timeZone)[0];
    if (!offset) continue;
    const instant = Date.parse(value + offset);
    if (instant > start && instant < end)
      ticks.push({ label: hour === 12 ? 'Noon' : hour === 6 ? '6 am' : '6 pm', left: (instant - start) / (end - start) * 100 });
  }
  ticks.push({ label: '12 am', left: 100 });
  return ticks;
}

export function csvCell(value: string | number): string {
  let text = String(value);
  // Prevent spreadsheet formulas when exporting user-authored names and descriptions.
  if (/^[\s]*[=+@-]/.test(text) || /^[\t\r\n]/.test(text)) text = "'" + text;
  return '"' + text.replace(/"/g, '""') + '"';
}

export function projectReportMinutes(report: TimecardReport, project: Pick<TimecardProject, 'id' | 'name'>) {
  if (!project.id || report.days.some(day => day.projectId !== project.id))
    throw new Error('Select a single project before exporting');
  const elapsedByDate = new Map<string, { actual: number; billable: number }>();
  const days = report.days.slice().sort((a, b) => a.date.localeCompare(b.date) || Number(b.entryBillable) - Number(a.entryBillable)).map(day => {
    const elapsed = elapsedByDate.get(day.date) || { actual: 0, billable: 0 };
    // Carry each day's partial minutes across its split rows so grouping doesn't change totals.
    const actualMinutes = Math.floor((elapsed.actual + day.seconds) / 60) - Math.floor(elapsed.actual / 60);
    const billableMinutes = Math.floor((elapsed.billable + day.billableSeconds) / 60) - Math.floor(elapsed.billable / 60);
    elapsedByDate.set(day.date, { actual: elapsed.actual + day.seconds, billable: elapsed.billable + day.billableSeconds });
    return { date: day.date, entryBillable: day.entryBillable, actualMinutes, billableMinutes, descriptions: day.descriptions };
  });
  return {
    days,
    actualMinutes: days.reduce((sum, day) => sum + day.actualMinutes, 0),
    billableMinutes: days.reduce((sum, day) => sum + day.billableMinutes, 0),
  };
}

const minuteTime = (minutes: number) => `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;

export function reportCsv(report: TimecardReport, project: Pick<TimecardProject, 'id' | 'name'>): string {
  const totals = projectReportMinutes(report, project);
  const rows: (string | number)[][] = [
    ['Timecard', report.name], ['Project', project.name],
    ['Start date', report.startDate], ['End date', report.endDate],
    ['Generated at', localTime(report.generatedAt, report.timeZone).slice(0, 16).replace('T', ' ')],
    ['Billing maximum (hours)', report.billingMaxMinutes == null ? 'No maximum' : report.billingMaxMinutes / 60],
    ['Status', report.includesRunningEntry ? 'DRAFT — includes running work through generation time' : 'Completed entries'],
    [], ['Date', 'Actual (h:mm)', 'Billable (h:mm)', 'Work Performed'],
  ];
  // Export whole minutes per day, with totals that reconcile to the displayed rows.
  totals.days.forEach(day => {
    rows.push([day.date, minuteTime(day.actualMinutes), minuteTime(day.billableMinutes), day.descriptions.join('\n')]);
  });
  rows.push([], ['Total', minuteTime(totals.actualMinutes), minuteTime(totals.billableMinutes), '']);
  return '\uFEFF' + rows.map(row => row.map(csvCell).join(',')).join('\r\n');
}

export interface PrintableReportOptions {
  hourlyRateCents?: number | null;
  details?: string;
  grouping?: 'daily' | 'weekly';
  weekStartsOn?: string;
}

export function reportDate(date: string): string {
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(date + 'T12:00:00Z'));
}

export function printableReportRows(report: TimecardReport, project: Pick<TimecardProject, 'id' | 'name'>, options: PrintableReportOptions = {}) {
  const totals = projectReportMinutes(report, project);
  const orderedDays = totals.days.slice().sort((a, b) => a.date.localeCompare(b.date));
  if (options.grouping !== 'weekly')
    return orderedDays.map(day => ({ ...day, label: reportDate(day.date) }));
  const firstDay = Math.max(0, weekdays.findIndex(day => day.value === options.weekStartsOn));
  const weeks = new Map<string, { date: string; entryBillable: boolean; label: string; actualMinutes: number; billableMinutes: number; descriptions: string[] }>();
  orderedDays.forEach(day => {
    const weekday = (new Date(day.date + 'T12:00:00Z').getUTCDay() + 6) % 7;
    const start = shiftDate(day.date, -((weekday - firstDay + 7) % 7));
    const end = shiftDate(start, 6);
    const key = `${start}:${day.entryBillable}`;
    let week = weeks.get(key);
    if (!week) {
      week = {
        date: start,
        entryBillable: day.entryBillable,
        label: `${reportDate(start < report.startDate ? report.startDate : start)} - ${reportDate(end > report.endDate ? report.endDate : end)}`,
        actualMinutes: 0,
        billableMinutes: 0,
        descriptions: [],
      };
      weeks.set(key, week);
    }
    week.actualMinutes += day.actualMinutes;
    week.billableMinutes += day.billableMinutes;
    week.descriptions = week.descriptions.concat(day.descriptions);
  });
  return Array.from(weeks.values()).sort((a, b) => a.date.localeCompare(b.date) || Number(b.entryBillable) - Number(a.entryBillable));
}

export function reportHtml(report: TimecardReport, project: Pick<TimecardProject, 'id' | 'name'> & Partial<Pick<TimecardProject, 'reportDetails' | 'hourlyRateCents'>>, options: PrintableReportOptions = {}): string {
  const totals = projectReportMinutes(report, project);
  const escape = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  const dateRange = `${reportDate(report.startDate)} - ${reportDate(report.endDate)}`;
  const rate = options.hourlyRateCents === undefined ? project.hourlyRateCents : options.hourlyRateCents;
  const amount = rate == null ? null : invoiceAmountCents(totals.billableMinutes, rate);
  const details = options.details === undefined ? project.reportDetails || '' : options.details;
  const days = printableReportRows(report, project, options).map(day => `<tr>
    <td class="date">${escape(day.label)}</td>
    <td class="hours">${minuteTime(day.actualMinutes)}</td>
    <td class="hours">${minuteTime(day.billableMinutes)}</td>
    <td>${day.descriptions.length ? '<ul>' + day.descriptions.map(text => `<li>${escape(text)}</li>`).join('') + '</ul>' : '—'}</td>
  </tr>`).join('');
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">
<title>Invoice — ${escape(project.name)} — ${escape(dateRange)}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; padding: 24px; color: #111; background: #fff; font: 14px/1.45 Arial, sans-serif; }
  h1 { font-size: 22px; margin: 0 0 4px; overflow-wrap: anywhere; }
  p { margin: 4px 0; overflow-wrap: anywhere; }
  .meta { margin-bottom: 20px; }
  .invoice-total { margin-top: 20px; text-align: right; break-inside: avoid; }
  .amount-due { font-size: 20px; font-weight: 700; }
  .sender-details { white-space: pre-wrap; }
  .draft { border: 1px solid #777; padding: 8px; margin: 12px 0; }
  table { border-collapse: collapse; width: 100%; table-layout: fixed; }
  th, td { padding: 10px 8px; border-bottom: 1px solid #ccc; text-align: left; vertical-align: top; overflow-wrap: anywhere; }
  th { font-weight: 700; }
  thead th { border-bottom: 2px solid #444; }
  .date { width: 20%; }
  .weekly .date { width: 26%; }
  .hours { width: 14%; font-variant-numeric: tabular-nums; }
  ul { margin: 0; padding-left: 18px; }
  li { white-space: pre-wrap; }
  li + li { margin-top: 4px; }
  .total { font-weight: 700; border-top: 2px solid #444; }
  .total > :nth-child(-n+3) { border-bottom: 2px solid #444; }
  @media (max-width: 480px) { body { padding: 12px; font-size: 12px; } th, td { padding: 8px 4px; } .hours { width: 17%; } }
  @page { margin: 16mm; }
  @media print {
    body { padding: 0; font-size: 10pt; }
    thead { display: table-header-group; }
    tr { break-inside: avoid; }
    h1, .meta { break-after: avoid; }
  }
</style></head><body${options.grouping === 'weekly' ? ' class="weekly"' : ''}>
<h1>INVOICE</h1>
<p><strong>Bill To:</strong> ${escape(project.name)}</p>
<div class="meta">${details.trim() ? `<p class="sender-details">${escape(details)}</p>` : ''}
<p>${escape(dateRange)}</p></div>
${report.includesRunningEntry ? '<p class="draft"><strong>Draft</strong> — includes work still being tracked.</p>' : ''}
<table aria-label="Invoice time details"><thead><tr><th class="date" scope="col">${options.grouping === 'weekly' ? 'Week' : 'Date'}</th><th class="hours" scope="col">Actual</th><th class="hours" scope="col">Billable</th><th scope="col">Work Performed</th></tr></thead>
<tbody>${days || '<tr><td colspan="4">No time recorded for these dates.</td></tr>'}
<tr class="total"><th scope="row">Total</th><td class="hours">${minuteTime(totals.actualMinutes)}</td><td class="hours">${minuteTime(totals.billableMinutes)}</td><td></td></tr>
</tbody></table>
${amount == null || rate == null ? '' : `<div class="invoice-total"><p>Hourly rate: ${usd(rate)} USD</p><p class="amount-due">Total Amount Due: ${usd(amount)} USD</p></div>`}
</body></html>`;
}

export function hoursToMinutes(value: string | number | null): number | null {
  if (value == null || String(value).trim() === '') return null;
  const hours = Number(value);
  const minutes = Math.round(hours * 60);
  if (!Number.isFinite(hours) || hours < 0 || minutes > 2147483647)
    throw new Error('Enter a valid, nonnegative number of hours');
  return minutes;
}

export function suggestedBillingHours(project?: TimecardProject): number | '' {
  return project?.weeklyMaxMinutes == null ? '' : project.weeklyMaxMinutes * 5 / 60;
}

// Store rates as cents and round once, using the same whole billable minutes shown on the invoice.
export function hourlyRateToCents(value: string | number | null): number | null {
  if (value == null || String(value).trim() === '') return null;
  const text = String(value).trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) throw new Error('Enter a nonnegative USD rate with at most two decimal places');
  const [dollars, fraction = ''] = text.split('.');
  const cents = Number(dollars) * 100 + Number(fraction.padEnd(2, '0'));
  if (!Number.isSafeInteger(cents) || cents > 2147483647) throw new Error('Hourly rate is too large');
  return cents;
}

export function invoiceAmountCents(billableMinutes: number, hourlyRateCents: number): number {
  if (!Number.isSafeInteger(billableMinutes) || billableMinutes < 0 ||
      !Number.isSafeInteger(hourlyRateCents) || hourlyRateCents < 0 || hourlyRateCents > 2147483647 ||
      !Number.isSafeInteger(billableMinutes * hourlyRateCents)) throw new Error('Invalid invoice hours or rate');
  return Math.round(billableMinutes * hourlyRateCents / 60);
}

function usd(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}
